import type { OpenerKind } from "@loot-os/api-contract";
import type { ReactNode } from "react";

/**
 * Each kind's drawing on a 64×64 grid, stroked in `currentColor`. Keyed on `OpenerKind`, so a new
 * kind fails typecheck until it has one.
 */
const DRAWINGS: Record<OpenerKind, ReactNode> = {
  // A reel of three items passing under a pointer, the middle one won.
  spinner: (
    <>
      <path d="M28 7h8l-4 6z" />
      <path d="M28 57h8l-4-6z" />
      <rect x="3" y="21" width="15" height="22" rx="3" opacity="0.5" />
      <rect x="23.5" y="18" width="17" height="28" rx="3" />
      <rect x="46" y="21" width="15" height="22" rx="3" opacity="0.5" />
      <path d="M10.5 28.5l3 3.5-3 3.5-3-3.5z" opacity="0.5" />
      <path d="M32 26l5 6-5 6-5-6z" />
      <path d="M53.5 28.5l3 3.5-3 3.5-3-3.5z" opacity="0.5" />
    </>
  ),
  // A cube slamming onto a perspective grid, voxels breaking off it.
  neon_slam: (
    <>
      <path d="M32 8l14 7-14 7-14-7z" />
      <path d="M18 15v16l14 7V22" />
      <path d="M46 15v16l-14 7" />
      <path d="M4 48h56" />
      <path d="M9 55h46" opacity="0.6" />
      <path d="M32 48v12M21 48l-6 12M43 48l6 12" opacity="0.6" />
      <rect x="6" y="24" width="5" height="5" rx="0.5" />
      <rect x="53" y="20" width="5" height="5" rx="0.5" />
      <rect x="9" y="36" width="3" height="3" rx="0.5" opacity="0.6" />
      <rect x="52" y="33" width="3" height="3" rx="0.5" opacity="0.6" />
      <path d="M11 43l-5 1.5M53 43l5 1.5" />
    </>
  ),
  // A booster pack torn open along its crimp, its card rising out.
  neon_pack: (
    <>
      <path d="M23 24V10a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2v14" />
      <path d="M32 12.5l1.2 2.5 2.8.4-2 2 .5 2.8-2.5-1.3-2.5 1.3.5-2.8-2-2 2.8-.4z" />
      <path d="M16 26l4-2.5 4 2.5 4-2.5 4 2.5 4-2.5 4 2.5 4-2.5 4 2.5v29a2 2 0 0 1-2 2H18a2 2 0 0 1-2-2z" />
      <path d="M16 50h32" opacity="0.6" />
      <path d="M32 32l6 6-6 6-6-6z" />
      <path d="M50 9l6-2 2 6-6 2z" opacity="0.6" />
      <path d="M8 14l3 1M9 20l3-1" opacity="0.6" />
    </>
  ),
  // A product box on a plinth, its lid popped off and the item won rising in the flashes.
  retail_box: (
    <>
      <ellipse cx="32" cy="50" rx="20" ry="3.5" />
      <path d="M12 50v5c0 1.9 9 3.5 20 3.5s20-1.6 20-3.5v-5" opacity="0.6" />
      <rect x="19" y="32" width="26" height="17" rx="1.5" />
      <path d="M32 32v17" opacity="0.6" />
      <rect x="5" y="19" width="24" height="5" rx="1" transform="rotate(-22 17 21.5)" />
      <path d="M38 9l6 6-6 12-6-12z" />
      <path d="M32 15h12" opacity="0.6" />
      <path d="M54 8v8M50 12h8" />
      <path d="M52 25v4M50 27h4" opacity="0.6" />
    </>
  ),
};

/**
 * An outline drawing of an opener kind's animation, in the text color it sits in. Decorative:
 * the kind's name is always printed beside it.
 */
export function OpenerKindArt({ kind, className }: { kind: OpenerKind; className?: string }) {
  return (
    <svg
      viewBox="0 0 64 64"
      fill="none"
      stroke="currentColor"
      strokeWidth={1.5}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      className={className}
    >
      {DRAWINGS[kind]}
    </svg>
  );
}
